import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Plus, Layout, Trash2, Copy, Globe, GlobeLock, Loader2, Search, Upload, Download, Check, FolderPlus, X, Folder, ChevronLeft, ChevronRight,
    Pin, PanelLeftClose
} from 'lucide-react';
import { BoardTemplate, adminTemplateService } from '../../lib/api/templates';
import { ConfirmationModal } from '../../components/ConfirmationModal';

interface TemplatesSidebarProps {
    selectedTemplateId: string | null;
    onSelectTemplate: (template: BoardTemplate | null) => void;
    onCreateTemplate: () => void;
    refreshTrigger?: number;
    isCollapsed: boolean;
    onToggleCollapse: () => void;
    isOverlay?: boolean;
}

export const TemplatesSidebar: React.FC<TemplatesSidebarProps> = ({
    selectedTemplateId,
    onSelectTemplate,
    onCreateTemplate,
    refreshTrigger = 0,
    isCollapsed,
    onToggleCollapse,
    isOverlay = false
}) => {
    const [templates, setTemplates] = useState<BoardTemplate[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [templateToDelete, setTemplateToDelete] = useState<BoardTemplate | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [filter, setFilter] = useState<'all' | 'draft' | 'published'>('all');
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [isExporting, setIsExporting] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const dirInputRef = useRef<HTMLInputElement>(null);
    const [importSummary, setImportSummary] = useState<{ total: number, success: number, failed: number } | null>(null);
    const [currentFolder, setCurrentFolder] = useState<string | null>(null);
    const [isPublishingFolder, setIsPublishingFolder] = useState<string | null>(null);
    const [selectedFolderNames, setSelectedFolderNames] = useState<Set<string>>(new Set());
    const [folderToDelete, setFolderToDelete] = useState<string | null>(null);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);
    const [alertConfig, setAlertConfig] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        variant: 'danger' | 'warning' | 'info' | 'primary';
        onConfirm?: () => void;
    } | null>(null);

    // Sync prop selection with local multi-selection
    useEffect(() => {
        if (selectedTemplateId) {
            setSelectedIds(prev => {
                const newSet = new Set(prev);
                if (!newSet.has(selectedTemplateId) && newSet.size <= 1) {
                    return new Set([selectedTemplateId]);
                }
                return prev;
            });
        }
    }, [selectedTemplateId]);

    const fetchTemplates = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await adminTemplateService.getTemplates();
            setTemplates(data);
        } catch (error) {
            console.error('Error fetching templates:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTemplates();
    }, [fetchTemplates, refreshTrigger]);

    const handleDelete = async () => {
        if (!templateToDelete) return;
        setIsDeleting(true);
        try {
            await adminTemplateService.deleteTemplate(templateToDelete.id);
            setTemplates(prev => prev.filter(t => t.id !== templateToDelete.id));
            if (selectedTemplateId === templateToDelete.id) {
                onSelectTemplate(null);
            }
        } catch (error) {
            console.error('Error deleting template:', error);
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false);
            setTemplateToDelete(null);
        }
    };

    const handleDuplicate = async (template: BoardTemplate) => {
        try {
            const duplicated = await adminTemplateService.duplicateTemplate(template.id);
            setTemplates(prev => [duplicated, ...prev]);
            onSelectTemplate(duplicated);
        } catch (error) {
            console.error('Error duplicating template:', error);
        }
    };

    const filteredTemplates = templates.filter(template => {
        const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesFilter = filter === 'all' || template.status === filter;
        const matchesFolder = currentFolder === null || template.templateType === currentFolder;
        return matchesSearch && matchesFilter && matchesFolder;
    });

    // Auto-selection logic
    useEffect(() => {
        if (!isLoading && filteredTemplates.length > 0) {
            const isCurrentlySelectedInView = filteredTemplates.some(t => t.id === selectedTemplateId);
            if (!selectedTemplateId || !isCurrentlySelectedInView) {
                onSelectTemplate(filteredTemplates[0]);
            }
        }
    }, [isLoading, filteredTemplates, selectedTemplateId, onSelectTemplate]);

    const getFolders = () => {
        // Only show folders that have templates matching the current search/status filter
        const foldersInView = Array.from(new Set(filteredTemplates.map(t => t.templateType)));

        return foldersInView.map(folder => {
            const allFolderTemplates = templates.filter(t => t.templateType === folder);
            return {
                name: folder,
                count: allFolderTemplates.length,
                publishedCount: allFolderTemplates.filter(t => t.status === 'published').length
            };
        });
    };

    const handlePublishFolder = async (folderName: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setAlertConfig({
            isOpen: true,
            title: 'Publish Folder',
            message: `Are you sure you want to publish all templates in "${folderName}"?`,
            variant: 'primary',
            onConfirm: async () => {
                setAlertConfig(null);
                setIsPublishingFolder(folderName);
                try {
                    const folderTemplates = templates.filter(t => t.templateType === folderName && t.status !== 'published');
                    const idsToPublish = folderTemplates.map(t => t.id);
                    if (idsToPublish.length > 0) {
                        await adminTemplateService.bulkTemplates('publish', idsToPublish);
                    }
                    await fetchTemplates();
                } catch (error) {
                    console.error('Failed to publish folder:', error);
                    setAlertConfig({
                        isOpen: true,
                        title: 'Error',
                        message: 'Failed to publish some templates in the folder',
                        variant: 'danger'
                    });
                } finally {
                    setIsPublishingFolder(null);
                }
            }
        });
    };

    const handleDuplicateFolder = async (folderName: string, e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            const folderTemplates = templates.filter(t => t.templateType === folderName);
            const newFolderName = `${folderName} (Copy)`;
            await Promise.all(folderTemplates.map(async (t) => {
                const duplicated = await adminTemplateService.duplicateTemplate(t.id);
                // Update the type of the duplicated template
                await adminTemplateService.updateTemplate(duplicated.id, {
                    name: duplicated.name, // Keep existing name
                    templateType: newFolderName as any
                });
            }));
            await fetchTemplates();
        } catch (error) {
            console.error('Failed to duplicate folder:', error);
            setAlertConfig({
                isOpen: true,
                title: 'Error',
                message: 'Failed to duplicate folder',
                variant: 'danger'
            });
        }
    };

    const handleDeleteFolder = async () => {
        const foldersToDelete = folderToDelete ? [folderToDelete] : Array.from(selectedFolderNames);
        if (foldersToDelete.length === 0) return;

        setIsBulkDeleting(true);
        try {
            const templatesToDelete = templates.filter(t => foldersToDelete.includes(t.templateType));
            const idsToDelete = templatesToDelete.map(t => t.id);
            if (idsToDelete.length > 0) {
                await adminTemplateService.bulkTemplates('delete', idsToDelete);
            }

            setTemplates(prev => prev.filter(t => !foldersToDelete.includes(t.templateType)));
            if (currentFolder && foldersToDelete.includes(currentFolder)) {
                setCurrentFolder(null);
            }
            setSelectedFolderNames(new Set());
            setSelectedIds(new Set());
            onSelectTemplate(null);
        } catch (error) {
            console.error('Failed to delete folder(s):', error);
            setAlertConfig({
                isOpen: true,
                title: 'Error',
                message: 'Failed to delete some folders',
                variant: 'danger'
            });
        } finally {
            setIsBulkDeleting(false);
            setFolderToDelete(null);
            setShowDeleteModal(false);
        }
    };

    const handleFolderClick = (folderName: string, e: React.MouseEvent) => {
        const isCmdPressed = e.metaKey || e.ctrlKey;
        if (isCmdPressed) {
            setSelectedFolderNames(prev => {
                const newSet = new Set(prev);
                if (newSet.has(folderName)) {
                    newSet.delete(folderName);
                } else {
                    newSet.add(folderName);
                }
                return newSet;
            });
        } else {
            setCurrentFolder(folderName);
            setSelectedFolderNames(new Set());
        }
    };

    const handleTemplateClick = (template: BoardTemplate, e: React.MouseEvent) => {
        const isCmdPressed = e.metaKey || e.ctrlKey;
        const isShiftPressed = e.shiftKey;

        if (isCmdPressed) {
            setSelectedIds(prev => {
                const newSet = new Set(prev);
                if (newSet.has(template.id)) {
                    newSet.delete(template.id);
                } else {
                    newSet.add(template.id);
                }
                return newSet;
            });
            // If we just selected it, show it in editor
            if (!selectedIds.has(template.id)) {
                onSelectTemplate(template);
            }
        } else if (isShiftPressed && selectedTemplateId) {
            // Simple range selection logic
            const lastIndex = filteredTemplates.findIndex(t => t.id === selectedTemplateId);
            const currentIndex = filteredTemplates.findIndex(t => t.id === template.id);
            if (lastIndex !== -1 && currentIndex !== -1) {
                const start = Math.min(lastIndex, currentIndex);
                const end = Math.max(lastIndex, currentIndex);
                const range = filteredTemplates.slice(start, end + 1).map(t => t.id);
                setSelectedIds(new Set(range));
                onSelectTemplate(template);
            }
        } else {
            setSelectedIds(new Set([template.id]));
            onSelectTemplate(template);
        }
    };

    const handleExport = async () => {
        if (selectedIds.size === 0) return;
        setIsExporting(true);
        try {
            await adminTemplateService.exportTemplates(Array.from(selectedIds));
        } catch (error) {
            console.error('Export failed:', error);
            setAlertConfig({
                isOpen: true,
                title: 'Export Failed',
                message: 'Failed to export templates',
                variant: 'danger'
            });
        } finally {
            setIsExporting(false);
        }
    };

    const handleImportClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        fileInputRef.current?.click();
    };

    const handleDirImportClick = () => {
        if (dirInputRef.current) {
            dirInputRef.current.value = '';
        }
        dirInputRef.current?.click();
    };

    const preScreenFiles = async (files: FileList | null): Promise<File[]> => {
        if (!files) return [];
        const validFiles: File[] = [];

        // Check if we have many files, we might want to show a loader or something
        // but for now we just process them.
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const fileName = file.name.toLowerCase();

            // Ignore hidden files (like .DS_Store)
            if (file.name.startsWith('.')) continue;

            // Pre-screen JSON files
            if (fileName.endsWith('.json')) {
                try {
                    const text = await file.text();
                    const data = JSON.parse(text);
                    // Check if it's a valid Dashbal board template
                    if (data && (data.name || data.type === 'dashbal_board_template' || data.chartConfig)) {
                        validFiles.push(file);
                    }
                } catch (e) {
                    // Not a valid JSON or template, skip
                    console.debug(`Skipping ${file.name}: Not a valid template JSON`);
                }
            } else if (fileName.endsWith('.zip')) {
                // ZIPs are pre-screened on the backend
                validFiles.push(file);
            }
        }
        return validFiles;
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        setIsImporting(true);
        setImportSummary(null);

        try {
            const validFiles = await preScreenFiles(files);

            if (validFiles.length === 0) {
                setAlertConfig({
                    isOpen: true,
                    title: 'No Valid Files',
                    message: 'No valid template files (.json) found in the selection.',
                    variant: 'warning'
                });
                return;
            }

            const results = await adminTemplateService.importTemplates(validFiles);
            setImportSummary({
                total: results.total,
                success: results.success,
                failed: results.failed
            });

            fetchTemplates(); // Refresh list

            // Clear summary after 5 seconds
            setTimeout(() => setImportSummary(null), 5000);

        } catch (error: any) {
            console.error('Import failed:', error);
            setAlertConfig({
                isOpen: true,
                title: 'Import Failed',
                message: error.message || 'Unknown error occurred during import',
                variant: 'danger'
            });
        } finally {
            setIsImporting(false);
            e.target.value = ''; // Reset input
        }
    };

    const draftCount = templates.filter(t => t.status === 'draft').length;
    const publishedCount = templates.filter(t => t.status === 'published').length;

    // Generate a color based on index for preview
    const getPreviewColor = (i: number) => {
        const colors = [
            "bg-blue-50 border-blue-100 text-blue-500",
            "bg-emerald-50 border-emerald-100 text-emerald-500",
            "bg-violet-50 border-violet-100 text-violet-500",
            "bg-amber-50 border-amber-100 text-amber-500",
            "bg-rose-50 border-rose-100 text-rose-500",
            "bg-indigo-50 border-indigo-100 text-indigo-500"
        ];
        return colors[i % colors.length];
    };

    return (
        <div className="h-full flex flex-col bg-white border-r border-neutral-200">
            {/* Header */}
            <div className={`p-4 border-b border-neutral-200 flex-shrink-0 ${isCollapsed ? 'flex flex-col items-center gap-4' : ''}`}>
                <div className={`flex items-center ${isCollapsed ? 'flex-col-reverse gap-4' : 'justify-between mb-3'}`}>
                    {!isCollapsed && <h2 className="text-sm font-semibold text-neutral-900">Templates</h2>}

                    <div className={`flex items-center ${isCollapsed ? 'flex-col gap-2' : 'gap-1'}`}>
                        {/* Collapse/Expand Toggle — always visible so a collapsed sidebar can be re-expanded without relying on hover overlay */}
                        <button
                            onClick={onToggleCollapse}
                            className="p-1.5 rounded-lg text-neutral-500 hover:bg-neutral-100 transition-colors"
                            title={isCollapsed ? 'Expand Sidebar' : (isOverlay ? 'Pin Sidebar' : 'Collapse Sidebar')}
                        >
                            {isCollapsed ? <ChevronRight size={18} /> : (isOverlay ? <Pin size={18} /> : <PanelLeftClose size={18} />)}
                        </button>

                        {!isCollapsed && (
                            <>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    accept=".json,application/json,.zip,application/zip,application/x-zip-compressed"
                                    multiple
                                    className="hidden"
                                />
                                <input
                                    type="file"
                                    ref={dirInputRef}
                                    onChange={handleFileChange}
                                    {...({
                                        webkitdirectory: "true",
                                        directory: "true",
                                        multiple: true
                                    } as any)}
                                    className="hidden"
                                />
                                <button
                                    onClick={handleImportClick}
                                    className="p-1.5 rounded-lg text-neutral-500 hover:bg-neutral-100 transition-colors"
                                    title="Import Files"
                                >
                                    {isImporting ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                                </button>
                                <button
                                    onClick={handleDirImportClick}
                                    className="p-1.5 rounded-lg text-neutral-500 hover:bg-neutral-100 transition-colors"
                                    title="Import Folder"
                                >
                                    {isImporting ? <Loader2 size={14} className="animate-spin" /> : <FolderPlus size={14} />}
                                </button>
                                {(selectedIds.size > 0 || selectedFolderNames.size > 0) && (
                                    <button
                                        onClick={selectedFolderNames.size > 0 ? () => setShowDeleteModal(true) : handleExport}
                                        className="p-1.5 rounded-lg text-neutral-500 hover:bg-neutral-100 transition-colors"
                                        title={selectedFolderNames.size > 0 ? "Delete Folders" : `Export ${selectedIds.size} Template${selectedIds.size > 1 ? 's' : ''}`}
                                    >
                                        {isExporting ? <Loader2 size={14} className="animate-spin" /> : (selectedFolderNames.size > 0 ? <Trash2 size={14} className="text-red-500" /> : <Download size={14} />)}
                                    </button>
                                )}
                                <div className="w-px h-4 bg-neutral-200 mx-1" />
                                <button
                                    onClick={onCreateTemplate}
                                    className="p-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                                    title="Create new template"
                                >
                                    <Plus size={14} />
                                </button>
                            </>
                        )}

                        {/* Show + button even when collapsed (optional, but good UX) */}
                        {isCollapsed && (
                            <button
                                onClick={onCreateTemplate}
                                className="p-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                                title="Create new template"
                            >
                                <Plus size={14} />
                            </button>
                        )}
                    </div>
                </div>

                {/* Search */}
                {!isCollapsed && (
                    <div className="relative">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
                        <input
                            type="text"
                            placeholder="Search templates..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-8 pr-3 py-1.5 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                        />
                    </div>
                )}
            </div>

            {/* Filter Tabs */}
            {!isCollapsed && (
                <div className="flex p-2 gap-1 border-b border-neutral-100 flex-shrink-0">
                    {[
                        { key: 'all' as const, label: 'All', count: templates.length },
                        { key: 'draft' as const, label: 'Draft', count: draftCount },
                        { key: 'published' as const, label: 'Published', count: publishedCount },
                    ].map(({ key, label, count }) => (
                        <button
                            key={key}
                            onClick={() => {
                                setFilter(key);
                                setCurrentFolder(null);
                            }}
                            className={`flex-1 py-1.5 px-2 text-[10px] font-medium rounded-md transition-colors ${filter === key
                                ? 'bg-blue-50 text-blue-600'
                                : 'text-neutral-500 hover:bg-neutral-50'
                                }`}
                        >
                            {label} ({count})
                        </button>
                    ))}
                </div>
            )}

            {/* Template List or Folder Explorer */}
            <div className="flex-1 overflow-y-auto p-2 space-y-1.5 min-h-0">
                {importSummary && (
                    <div className="p-2 mb-2 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-between animate-in fade-in slide-in-from-top-2 duration-300">
                        <div className="flex items-center gap-2">
                            <div className="p-1 bg-blue-100 rounded text-blue-600">
                                <Check size={12} />
                            </div>
                            <div className="text-[10px] font-medium text-blue-800 leading-tight">
                                <div>Import complete</div>
                                <div className="text-blue-600/70 font-normal">
                                    {importSummary.success} successful, {importSummary.failed} skipped
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={() => setImportSummary(null)}
                            className="p-1 text-blue-400 hover:text-blue-600 hover:bg-blue-100 rounded-md transition-colors"
                        >
                            <X size={12} />
                        </button>
                    </div>
                )}

                {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 size={20} className="animate-spin text-neutral-400" />
                    </div>
                ) : currentFolder === null ? (
                    /* Root View - Folders */
                    getFolders().map((folder, idx) => (
                        <div
                            key={folder.name}
                            onClick={(e) => handleFolderClick(folder.name, e)}
                            className={`group relative flex flex-col rounded-lg border transition-all cursor-pointer ${selectedFolderNames.has(folder.name)
                                ? 'border-blue-500 bg-blue-50/50 shadow-sm ring-1 ring-blue-500'
                                : 'border-neutral-200 hover:border-blue-300 hover:bg-neutral-50'
                                }`}
                            title={isCollapsed ? folder.name : undefined}
                        >
                            <div className={`h-16 ${isCollapsed ? 'rounded-lg' : 'rounded-t-lg'} flex items-center justify-center ${getPreviewColor(idx)} bg-opacity-30 relative`}>
                                <Folder size={32} className="opacity-40" />

                                {selectedFolderNames.has(folder.name) && selectedFolderNames.size > 1 && (
                                    <div className="absolute top-1 left-1 bg-blue-500 text-white rounded-full p-0.5 shadow-sm z-10">
                                        <Check size={8} strokeWidth={3} />
                                    </div>
                                )}

                                {!isCollapsed && (
                                    <div className="absolute top-1 right-1">
                                        {folder.publishedCount === folder.count && folder.count > 0 ? (
                                            <span className="flex items-center gap-0.5 px-1 py-0.5 text-[9px] font-medium bg-emerald-500 text-white rounded">
                                                <Globe size={8} />
                                                All Live
                                            </span>
                                        ) : folder.publishedCount > 0 ? (
                                            <span className="flex items-center gap-0.5 px-1 py-0.5 text-[9px] font-medium bg-blue-500 text-white rounded">
                                                <Globe size={8} />
                                                {folder.publishedCount}/{folder.count} Live
                                            </span>
                                        ) : (
                                            <span className="flex items-center gap-0.5 px-1 py-0.5 text-[9px] font-medium bg-neutral-800 text-white rounded">
                                                <GlobeLock size={8} />
                                                Drafts
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                            {!isCollapsed && (
                                <div className="p-2 flex items-center justify-between">
                                    <div>
                                        <p className="text-xs font-medium text-neutral-900 truncate">{folder.name}</p>
                                        <p className="text-[10px] text-neutral-500">{folder.count} templates</p>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDuplicateFolder(folder.name, e);
                                                }}
                                                className="p-1 bg-white border border-neutral-200 rounded shadow-sm text-neutral-500 hover:text-blue-600"
                                                title="Duplicate Folder"
                                            >
                                                <Copy size={10} />
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setFolderToDelete(folder.name);
                                                    setShowDeleteModal(true);
                                                }}
                                                className="p-1 bg-white border border-neutral-200 rounded shadow-sm text-neutral-500 hover:text-red-600"
                                                title="Delete Folder"
                                            >
                                                <Trash2 size={10} />
                                            </button>
                                        </div>
                                        <button
                                            onClick={(e) => handlePublishFolder(folder.name, e)}
                                            disabled={isPublishingFolder === folder.name || folder.publishedCount === folder.count}
                                            className="p-1.5 text-[10px] font-medium text-blue-600 hover:bg-blue-100 rounded flex items-center gap-1 transition-colors disabled:opacity-50"
                                        >
                                            {isPublishingFolder === folder.name ? <Loader2 size={10} className="animate-spin" /> : <Globe size={10} />}
                                            Publish
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))
                ) : (
                    /* Nested View - Templates */
                    <>
                        <div className="flex items-center gap-2 mb-2 p-1 text-xs font-medium text-neutral-500">
                            <Folder size={14} className="text-blue-500" />
                            <span>{currentFolder}</span>
                        </div>
                        {filteredTemplates.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-8 text-neutral-400">
                                <Layout size={24} className="mb-2 opacity-50" />
                                <p className="text-xs">No templates in this folder</p>
                            </div>
                        ) : (
                            filteredTemplates.map((template, idx) => (
                                <div
                                    key={template.id}
                                    onClick={(e) => handleTemplateClick(template, e)}
                                    className={`group relative flex flex-col rounded-lg border cursor-pointer transition-all ${selectedIds.has(template.id)
                                        ? 'border-blue-500 bg-blue-50/50 shadow-sm ring-1 ring-blue-500'
                                        : 'border-neutral-200 hover:border-blue-300 hover:bg-neutral-50'
                                        }`}
                                    title={isCollapsed ? template.name : undefined}
                                >
                                    {/* Preview Area */}
                                    <div className={`h-16 ${isCollapsed ? 'rounded-lg' : 'rounded-t-lg'} relative overflow-hidden ${getPreviewColor(idx)}`}>
                                        <div className="w-full h-full p-1.5 relative">
                                            {Array.isArray(template.chartConfig) && template.chartConfig.length > 0 ? (
                                                template.chartConfig.slice(0, 6).map((item: any, itemIdx: number) => {
                                                    const maxRows = 4;
                                                    const wPercent = Math.min((item.w / 12) * 100, 100);
                                                    const xPercent = (item.x / 12) * 100;
                                                    const hPercent = Math.min((item.h / maxRows) * 100, 100);
                                                    const yPercent = (item.y / maxRows) * 100;
                                                    if (item.y >= maxRows) return null;

                                                    return (
                                                        <div
                                                            key={`preview-${template.id}-${itemIdx}`}
                                                            className="absolute bg-white/80 border border-black/5 rounded-[2px] shadow-sm"
                                                            style={{
                                                                left: `${xPercent}%`,
                                                                top: `${yPercent}%`,
                                                                width: `${wPercent}%`,
                                                                height: `${hPercent}%`,
                                                            }}
                                                        />
                                                    );
                                                })
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <Layout size={16} className="opacity-30" />
                                                </div>
                                            )}
                                        </div>

                                        {/* Status Badge */}
                                        {!isCollapsed && (
                                            <div className="absolute top-1 right-1">
                                                {template.status === 'published' ? (
                                                    <span className="flex items-center gap-0.5 px-1 py-0.5 text-[9px] font-medium bg-emerald-500 text-white rounded">
                                                        <Globe size={8} />
                                                        Live
                                                    </span>
                                                ) : (
                                                    <span className="flex items-center gap-0.5 px-1 py-0.5 text-[9px] font-medium bg-neutral-800 text-white rounded">
                                                        <GlobeLock size={8} />
                                                        Draft
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Selection Checkmark */}
                                    {selectedIds.has(template.id) && selectedIds.size > 1 && (
                                        <div className="absolute top-1 left-1 bg-blue-500 text-white rounded-full p-0.5 shadow-sm z-10">
                                            <Check size={8} strokeWidth={3} />
                                        </div>
                                    )}

                                    {/* Content */}
                                    {!isCollapsed && (
                                        <div className="p-2">
                                            <p className="text-xs font-medium text-neutral-900 truncate mb-0.5">
                                                {template.name}
                                            </p>
                                            <p className="text-[10px] text-neutral-500 truncate">
                                                {template.chartConfig?.length || 0} charts
                                            </p>
                                        </div>
                                    )}

                                    {/* Actions (on hover) */}
                                    {!isCollapsed && (
                                        <div className="absolute top-14 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDuplicate(template);
                                                }}
                                                className="p-1 bg-white rounded shadow-sm text-neutral-500 hover:text-blue-600"
                                                title="Duplicate"
                                            >
                                                <Copy size={10} />
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setTemplateToDelete(template);
                                                    setShowDeleteModal(true);
                                                }}
                                                className="p-1 bg-white rounded shadow-sm text-neutral-500 hover:text-red-600"
                                                title="Delete"
                                            >
                                                <Trash2 size={10} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </>
                )}
            </div>

            {/* Bottom Navigation Bar */}
            <div className="p-2 border-t border-neutral-100 bg-neutral-50/50 flex items-center justify-center gap-4">
                <button
                    onClick={() => setCurrentFolder(null)}
                    disabled={currentFolder === null}
                    className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-900 hover:bg-white border border-transparent hover:border-neutral-200 transition-all disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:border-transparent"
                    title="Back to root"
                >
                    <ChevronLeft size={16} />
                </button>
                <div className="w-px h-4 bg-neutral-200" />
                <button
                    className="p-1.5 rounded-lg text-neutral-400 cursor-not-allowed opacity-30"
                    disabled
                >
                    <ChevronRight size={16} />
                </button>
            </div>

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={showDeleteModal}
                onCancel={() => {
                    setShowDeleteModal(false);
                    setTemplateToDelete(null);
                    setFolderToDelete(null);
                }}
                onConfirm={folderToDelete || selectedFolderNames.size > 0 ? handleDeleteFolder : handleDelete}
                title={folderToDelete || selectedFolderNames.size > 0 ? "Delete Folder(s)" : "Delete Template"}
                message={folderToDelete
                    ? `Are you sure you want to delete the folder "${folderToDelete}" and all its templates? This action cannot be undone.`
                    : selectedFolderNames.size > 0
                        ? `Are you sure you want to delete ${selectedFolderNames.size} folder(s) and all their templates? This action cannot be undone.`
                        : `Are you sure you want to delete "${templateToDelete?.name}"? This action cannot be undone.`
                }
                confirmText={isDeleting || isBulkDeleting ? "Deleting..." : "Delete"}
                cancelText="Cancel"
                variant="danger"
            />

            {/* Alert/Info Modal */}
            <ConfirmationModal
                isOpen={!!alertConfig?.isOpen}
                onCancel={() => setAlertConfig(null)}
                onConfirm={alertConfig?.onConfirm || (() => setAlertConfig(null))}
                title={alertConfig?.title || ''}
                message={alertConfig?.message || ''}
                confirmText={alertConfig?.onConfirm ? 'Confirm' : 'Close'}
                cancelText={alertConfig?.onConfirm ? 'Cancel' : undefined}
                variant={alertConfig?.variant || 'info'}
            />
        </div>
    );
};
